import React, { useCallback, useRef } from "react";
import { Box } from "@mui/material";

const hideHorizontalScrollbarSx = {
    overflowX: "auto",
    overflowY: "hidden",
    WebkitOverflowScrolling: "touch",
    scrollbarWidth: "none",
    msOverflowStyle: "none",
    "&::-webkit-scrollbar": { display: "none" },
};

/**
 * Horizontal piece tray — fixed row count (column-major), scrolls sideways.
 * `containerRef` is attached to the scroll element (hit-testing / drag offsets).
 */
export default function PieceTrayScrollRows({
    children,
    containerRef,
    rowCount = 2,
    cellSizePx,
    maxWidthPx,
    gapPx = 0,
    sx,
}) {
    const scrollRef = useRef(null);
    const rows = Math.max(1, Math.floor(rowCount) || 1);
    const cell = Math.max(1, Math.round(cellSizePx) || 1);
    const gap = Math.max(0, Math.round(gapPx) || 0);
    const heightPx = rows * cell + Math.max(0, rows - 1) * gap;

    const setScrollEl = useCallback(
        (el) => {
            scrollRef.current = el;
            if (!containerRef) return;
            if (typeof containerRef === "function") {
                containerRef(el);
            } else {
                containerRef.current = el;
            }
        },
        [containerRef],
    );

    return (
        <Box
            ref={setScrollEl}
            sx={{
                width: "100%",
                maxWidth: maxWidthPx ?? "100%",
                height: heightPx,
                flexShrink: 0,
                position: "relative",
                pointerEvents: "auto",
                boxSizing: "border-box",
                touchAction: "pan-x",
                ...hideHorizontalScrollbarSx,
                ...sx,
            }}
        >
            <Box
                sx={{
                    display: "grid",
                    gridTemplateRows: `repeat(${rows}, ${cell}px)`,
                    gridAutoFlow: "column",
                    gridAutoColumns: `${cell}px`,
                    columnGap: `${gap}px`,
                    rowGap: `${gap}px`,
                    width: "max-content",
                    height: heightPx,
                    alignContent: "start",
                }}
            >
                {children}
            </Box>
        </Box>
    );
}
